import { SMART_LEAD_CAMPAIGN_ANALYTICS_URL } from '@aibees/elephant/constants/integration';
import { useExportFileDownload } from '@aibees/elephant/feature/exports-management-table/actions/export-file-action';
import { useRetryIntegrationApi } from '@aibees/elephant/feature/exports-management-table/api/use-retry-integration-api';
import { EditExportDialog } from '@aibees/elephant/feature/exports-management-table/components/edit-export-dialog';
import {
  ExportIntegrationStatus,
  ExportSearchGrouping,
} from '@aibees/elephant/hooks';
import { ROUTES as R } from '@aibees/elephant/router';
import { notificationIconMap } from '@aibees/elephant/utils';
import { PopoverListButtonProps } from '@aibees/shared/components';
import {
  useBoolean,
  useCopyToClipboard,
  useNotify,
} from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils/class-names';
import { random } from 'lodash-es';
import { memo } from 'react';
import { getExportActionMenuItemsOptions } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import {
  type ExportTableActionProps as Props,
  ExportTableActionType as Action,
} from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

export const ExportTableAction = memo(({ className, item }: Props) => {
  const notify = useNotify();
  const clip = useCopyToClipboard();
  const editSearchState = useBoolean(false);
  const integrationExportFailedDialogState = useBoolean(false);
  const retryIntegrationApi = useRetryIntegrationApi(item.id);
  const { downloadFile } = useExportFileDownload(item);

  const handleSelect: PopoverListButtonProps['onSelect'] = (e) => {
    if (e.key === Action.edit) editSearchState.onTrue();
    else if (e.key === Action.searchLink) {
      const exportId = encodeURIComponent(item.id);

      let url = '';
      if (item.grouping === ExportSearchGrouping.COMPANY) {
        url = `${R.searchCompany}?companies_exports_request_include=${item.name}%3A%3A${exportId}`;
      } else if (item.grouping === ExportSearchGrouping.PERSON) {
        url = `${R.searchPeople}?people_exports_request_include=${item.name}%3A%3A${exportId}`;
      }

      if (url) {
        window.open(url, '_blank');
      }
    } else if (e.key === Action.copyID) {
      clip.copy(item.id).then(() => {
        const key = Date.now().toString();

        notify.complete(
          'ID Copied',
          'The ID has been copied to your clipboard.',
          key,
          {
            icon: notificationIconMap.EXPORT,
          }
        );
      });
    } else if (e.key === Action.viewCampaign) {
      window.open(
        SMART_LEAD_CAMPAIGN_ANALYTICS_URL.replace(
          ':id',
          item.integrations.SMART_LEAD?.id ?? ''
        ),
        '_blank'
      );
    } else if (e.key === Action.retry) {
      if (
        item.integrations &&
        item.integrations.SMART_LEAD?.response?.leadLimitExhausted &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        integrationExportFailedDialogState.onTrue();
      } else if (
        item.integrations &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        retryIntegrationApi
          .mutateAsync({
            type: Object.keys(item.integrations)[0],
          })
          .then(() => {
            notify.progress(
              'Your export is in progress',
              `Adding ${item.records} Records...`,
              random(1, 101),
              { icon: notificationIconMap.EXPORT }
            );
          });
      }
    } else if (e.key === Action.downloadCSV) {
      if (
        item.integrations &&
        item.integrations.SMART_LEAD?.status === ExportIntegrationStatus.FAILED
      ) {
        downloadFile();
      }
    }
  };

  return (
    <>
      <Styled.Wrapper
        className={classnames('ExportTableAction', className)}
        icon="more-horizontal"
        variant="outlined"
        size="xsmall"
        popoverSx={SX.popover}
        contentSx={SX.content}
        itemSx={SX.item}
        itemDivider={true}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
        items={getExportActionMenuItemsOptions(item)}
        onSelect={handleSelect}
      />
      <EditExportDialog item={item} state={editSearchState} />
    </>
  );
});

ExportTableAction.displayName = 'ExportTableAction';
